import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { ContactField, Cryptocoin } from "@models";
import { combineLatest, EMPTY, Observable } from "rxjs";
import { MultichainContactFacade } from "@modules/main/components/multichain-contact/services/multichain-contact.facade";
import { map } from "rxjs/operators";
import { DestinationTagType } from "@modules/+new/payments/features/crypto-deposit/domain/entities/destination-tag-type";
import { containsString } from "@helpers/utils";
import { CryptocoinService } from "@main-services/currency";

@Component({
	selector: "multichain-contact-address-form",
	templateUrl: "./multichain-contact-address-form.component.html",
	styleUrls: ["./multichain-contact-address-form.component.scss"],
	standalone: false
})
export class MultichainContactAddressFormComponent implements OnInit {
	@Input() coinOptions: BpSelectOption[] = [];
	@Input() field: ContactField = null;
	@Input() isNewContact: boolean = true;
	@Output() deleteFieldEmitter: EventEmitter<void> = new EventEmitter<void>();
	@Output() saveContactEmitter: EventEmitter<void> = new EventEmitter<void>();
	public networkOptions$: Observable<BpSelectOption[]> = EMPTY;
	public destinationTagType$: Observable<string> = EMPTY;

	constructor(
		private readonly multichainService: MultichainContactFacade,
		private readonly cryptocoinService: CryptocoinService
	) {}

	ngOnInit(): void {
		this.refreshDefaultNetworkData();
	}

	public onCoinChange(type: string): void {
		this.field.cryptocoin_id = type;
		this.refreshDefaultNetworkData(true);
	}

	public onNetorwkChange(id: string): void {
		this.destinationTagType$ = this.getDestinationTagLabel(id);
	}

	public saveContact(): void {
		this.saveContactEmitter.emit();
	}

	public deleteAddress(): void {
		this.deleteFieldEmitter.emit();
	}

	private getDestinationTagLabel(networkId: string): Observable<string> {
		return this.multichainService.getNetworkByCoinAndNetworkId(this.field._cryptocoin_id, networkId).pipe(
			map((network) => {
				if (!network?.destination_tag) {
					return undefined;
				}
				if (network.destination_tag.type === DestinationTagType.DESTINATION_TAG) {
					return "the_destination_tag";
				}
				if (network.destination_tag.type === DestinationTagType.MEMO) {
					return "the_memo_title";
				}
				if (network.destination_tag.type === DestinationTagType.MESSAGE) {
					return "the_message_title";
				}
				return undefined;
			})
		);
	}

	private refreshDefaultNetworkData(coinChange: boolean = false): void {
		this.networkOptions$ = combineLatest([
			this.multichainService.getCoinById(this.field._cryptocoin_id),
			this.multichainService.getNetworksForSelectedCoin(this.field._cryptocoin_id),
			this.multichainService.getDefaultNetworkId(this.field._cryptocoin_id)
		]).pipe(
			map(([coin, networkOptions, defaultNetworkId]) => {
				if (this.isNewContact || this.field._coin_network_id?.length === 0 || coinChange) {
					this.field._coin_network_id = defaultNetworkId;
				}
				this.field._type = (coin.symbol + "_address").toLowerCase();
				this.destinationTagType$ = this.getDestinationTagLabel(defaultNetworkId);
				return networkOptions;
			})
		);
	}

	public getCustomSearch(): Function {
		return (searchInput: string, item: BpSelectOption) => {
			const id: string = item.value;
			const coin: Cryptocoin = this.getCoinFromFieldId(id);

			if (!coin) {
				return false;
			}
			if (containsString(coin.getSymbol(), searchInput)) {
				return true;
			}
			// By tradable name
			return containsString(coin.getName(), searchInput);
		};
	}

	private getCoinFromFieldId(id: string): Cryptocoin {
		return this.cryptocoinService.getItemById(id);
	}
}
